import { useState } from "react";

export default function AuditDemo() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [step, setStep] = useState("");
  const allSatisfied =
    result?.evidence_mapping?.every(
      (item) => item.status === "Satisfied"
  ) ?? false;

  const handleUpload = async () => {
    if (!file) return;

    setLoading(true);
    setResult(null);

    // Fake intelligent steps
    setStep("Analyzing access review records...");
    setTimeout(() => setStep("Identifying authorized users..."), 1000);
    setTimeout(() => setStep("Checking access change execution..."), 2000);
    setTimeout(() => setStep("Generating audit assessment..."), 3000);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await fetch("http://localhost:8000/demo/audit", {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      setTimeout(() => {
        setResult(data);
        setLoading(false);
        setStep("");
      }, 3500); // sync with steps
    } catch (err) {
      console.error(err);
      setLoading(false);
      setStep("Something went wrong");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 flex justify-center">
      <div className="w-full max-w-4xl">

        {/* Header */}
        <h1 className="text-2xl font-semibold mb-2">
          Fluxonix Audit Generator
        </h1>
        <p className="text-gray-600 mb-6">
          Generate audit-ready compliance reports in under 2 minutes
        </p>

        {/* Upload Card */}
        {!result && (
          <div className="bg-white rounded-xl shadow p-6 mb-6">
            <input
              type="file"
              onChange={(e) => setFile(e.target.files[0])}
              className="mb-4"
            />

            <button
              onClick={handleUpload}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Generate Audit Packet
            </button>
          </div>
        )}

        {/* Processing */}
        {loading && (
          <div className="bg-white rounded-xl shadow p-6 text-center">
            <p className="text-lg font-medium">{step}</p>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="bg-white rounded-xl shadow p-6 space-y-6">

            {/* Header */}
            <div>
              <h2 className="text-xl font-semibold">
                AC.L2-3.1.1 — Access Control
              </h2>
              <p className="text-gray-500">
                Authorized Access
              </p>
            </div>

            {/* Confidence */}
            <div className="text-3xl font-bold text-blue-600">
              {result.confidence}% Confidence
            </div>
            

            <div className={`text-lg font-semibold mt-2 ${
            allSatisfied ? "text-green-600" : "text-red-600"
            }`}>
            Audit Conclusion: {allSatisfied ? "Fully Satisfied" : "Not Fully Satisfied"}
            </div>

            {/* Narrative */}
            <div>
              <h3 className="font-semibold mb-2">Assessment Summary</h3>
              <p className="text-gray-700">{result.narrative}</p>
            </div>

            {/* Critical Gaps */}
            {result.gaps && result.gaps.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="font-semibold text-red-700 mb-2">
                ❗ Critical Gaps (Audit Failure Points)
                </h3>
                <ul className="list-disc pl-5 text-gray-800">
                {result.gaps.map((gap, idx) => (
                    <li key={idx}>{gap}</li>
                ))}
                </ul>
            </div>
            )}

            {!allSatisfied && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-gray-800">
                <strong>Insight:</strong> Access review was performed, but there is no evidence that decisions from the review were executed. This is a common audit failure pattern.
                </p>
            </div>
            )}

            {allSatisfied && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-gray-800">
                <strong>Insight:</strong> Access review, validation, and execution are all documented with traceable evidence. This meets audit expectations for this control.
                </p>
            </div>
            )}

            {/* Evidence Table */}
            <div>
              <h3 className="font-semibold mb-2">Evidence Mapping</h3>
              <table className="w-full border text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="p-2 text-left">Requirement</th>
                    <th className="p-2 text-left">Evidence</th>
                    <th className="p-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {result.evidence_mapping.map((row, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="p-2">{row.requirement}</td>
                      <td className="p-2">{row.evidence}</td>
                      <td className="p-2">
                        {row.status === "Satisfied" && "✅"}
                        {row.status === "Partial" && "⚠️"}
                        {row.status === "Missing" && "❌"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Actions */}
            <div>
              <h3 className="font-semibold mb-2">Recommended Actions</h3>
              <ul className="list-disc pl-5 text-gray-700">
                {result.actions.map((action, idx) => (
                  <li key={idx}>{action}</li>
                ))}
              </ul>
            </div>

            {/* Reset */}
            <button
              onClick={() => setResult(null)}
              className="mt-4 text-blue-600 underline"
            >
              Run another analysis
            </button>
          </div>
        )}
      </div>
    </div>
  );
}